import {List, Map, Record, Set} from 'immutable';
import {Moment} from 'moment';
import {Property} from 'csstype';

/**
 * CSS Colors
 */
export type Color = Property.Color;

/**
 * A type which infers the id type for the given model
 * e.g. IdMap<KpiTarget> is equivalent to Map<KpiTargetId, KpiTarget>
 *
 */
export type IdMap<T extends {id: any}> = Map<T['id'], T>;

/**
 * A "function-like" type which is natively extended by all functions.
 * The `Function` class native to typescript doesn't play nice, so we use this
 * instead when defining types which operate over functions of an unknown type.
 */
export type Func = (...args: any[]) => any;

/**
 * Alt takes Class definitions as parameters, rather than already constructed classes
 * This defines a generic shape for that
 */
export type UninstantiatedClass<Type> = {
  new (): Type;
};

// These don't work obviously, but are useful for documentation purposes.
export type Int = number;
export type Double = number;
// A PctDouble is a Double between 0 and 1 which may be multiplied by 100 to get a percentage
export type PctDouble = number;

// Time unit type aliases
export type Milliseconds = number;
export type Seconds = number;
export type Minutes = number;
export type Hours = number;

export type nil = null | undefined;

// When building records, often times we have default js data structures which are converted to
// immutable ones. This type is used in record constructors to allow inputs to use these alternative
// structures, but not affect the record's actual usage.
//
// Also makes all fields optional
export type RecordProps<Type> = {
  [Prop in keyof Type]?: Type[Prop] extends List<infer Element>
    ? // Is it a List?
      Element[] | List<Element>
    : // Is it a Set?
    Type[Prop] extends Set<infer Element>
    ? Element[] | Set<Element>
    : // Is it a Moment?
    Type[Prop] extends Moment
    ? Moment | string // Moments coming from anywhere else are usually strings
    : // Is it a Data Class?
    Type[Prop] extends DataClass<infer NestedType>
    ? RecordProps<NestedType> // Recursing for nested data classes
    : // Is it a Record?
    Type[Prop] extends Record<any, any>
    ? any // Records simply dont have any type info to infer, cast these as needed.
    : // Is it a Map?
    Type[Prop] extends Map<string, infer Element>
    ? {[index: string]: Element} | Map<string, Element>
    : // Is it a number map?
    Type[Prop] extends Map<number, infer Element>
    ? {[index: number]: Element} | Map<number, Element>
    : // Keys are optional
      Type[Prop];
};

// A string hex code for CSS Colors
export type HexCode = string;

export interface DataClass<T> {
  new (): T & RecordInterface<T>;
  new (values: RecordProps<T>): T & RecordInterface<T>;

  (): T & RecordInterface<T>;
  (values: RecordProps<T>): T & RecordInterface<T>;
}
type RecordKey = number | string | undefined | null;
interface RecordInterface<T> {
  get<K extends keyof T>(key: K): T[K];
  get<K extends keyof T, F>(key: K, fallback?: F): T[K] | F;
  getIn<U>(keys: RecordKey[]): U;
  getIn<U, F>(keys: RecordKey[], fallback?: F): U | F;
  set<K extends keyof T>(key: K, val: T[K]): this;
  set(key: RecordKey, val: any): this;
  setIn(key: RecordKey[], val: any): this;
  update<K extends keyof T>(keyPath: K, updater: (value?: T[K]) => T[K]): this;
  update(key: RecordKey, updater: (value?: any) => any): this;
  updateIn(key: RecordKey[], updater: (value?: any) => any): this;
  delete<K extends keyof T>(key: K): this;
  delete(key: RecordKey): this;
  deleteIn(key: RecordKey[]): this;
  merge(values: Partial<T>): this;
  equals(value: this): boolean;
  equals(value: unknown): value is this;
  toJS(): T;
}

/**
 * Immutable records but with types
 *
 * @link KpiAssignment - example usage
 */
export function Data<T>(defaultValues: AllowNil<Required<T>>): DataClass<T> {
  return Record(defaultValues) as any;
}

type AllowNil<Type> = {
  [Prop in keyof Type]: Type[Prop] | nil;
};
